import React, { useState, useRef, useEffect } from "react"
import { Link } from "gatsby"
import { motion } from "framer-motion"
import { Container, Flex } from "../styles/globalStyles"
import {
  Nav,
  NavHeader,
  CloseNav,
  NavList,
  NavFooter,
  NavVideos,
} from "../styles/navigationStyles"
import { FooterContent, FooterSocial } from "../styles/footerStyles"
import { Instagram, Facebook, Vimeo } from "../assets/svg/social-icons"

const navRoutes = [
  {
    id: 0,
    title: "not humble",
    path: "/not-humble",
    video: "featured-video.mp4",
  },
  { id: 1, title: "bleeping easy", path: "/bleeping-easy", video: "easy.mp4" },
  {
    id: 2,
    title: "make it zero",
    path: "/make-it-zero",
    video: "make-it-zero.mp4",
  },
  {
    id: 3,
    title: "it takes an island",
    path: "/it-takes-an-island",
    video: "it-takes-an-island.mp4",
  },
  { id: 4, title: "50 beaches", path: "/50-beaches", video: "50-beaches.mp4" },
]

const Navigation = ({ toggleMenu, setToggleMenu, onCursor }) => {
  const [revealVideo, setRevealVideo] = useState({
    show: false,
    video: "featured-video.mp4",
    key: 0,
  })
  const videoRef = useRef(null)

  // Swap the source on the same persistent <video> element instead of
  // mounting a new one per link. Mobile Safari is unreliable at starting
  // playback on a brand-new video node (especially after the exit-animation
  // delay from the old remount-based approach), which left the panel black
  // when switching clips even though desktop Safari never showed the issue.
  //
  // Visibility is NOT gated on canplay/play succeeding — the video box
  // stays visible regardless, and playback is retried independently. Tying
  // opacity to a "ready" flag that depends on canplay firing left the panel
  // permanently black whenever canplay was slow or never fired.
  useEffect(() => {
    const el = videoRef.current
    if (!el) return

    el.muted = true
    el.defaultMuted = true
    el.setAttribute("muted", "")
    el.playsInline = true
    el.setAttribute("playsinline", "")
    el.src = require(`../assets/video/${revealVideo.video}`)
    el.load()

    const tryPlay = () => el.play().catch(() => {})
    tryPlay()
    el.addEventListener("canplay", tryPlay)
    return () => el.removeEventListener("canplay", tryPlay)
  }, [revealVideo.video])

  // Safety net: iOS Safari can be inconsistent about which specific tap's
  // gesture window a given play() call lands in. Rather than chase that
  // timing precisely, retry playback on every touch/click anywhere on the
  // page — any tap is a valid opportunity to resume a paused video, so this
  // catches whichever attempts the effect above missed.
  useEffect(() => {
    const resume = () => {
      const el = videoRef.current
      if (el && el.paused) {
        el.play().catch(() => {})
      }
    }
    document.addEventListener("touchend", resume)
    document.addEventListener("click", resume)
    return () => {
      document.removeEventListener("touchend", resume)
      document.removeEventListener("click", resume)
    }
  }, [])

  return (
    <>
      {
        // Always mounted (never conditionally rendered) so the video
        // element already exists in the DOM by the time the hamburger is
        // tapped — see the onClick handler in header.js, which relies on
        // this element existing to fire an in-gesture play() synchronously.
        // Visibility is handled purely by the slide animation + inert props.
      }
      <Nav
        animate={{ x: toggleMenu ? 0 : "-100%" }}
        transition={{ duration: 0.8, ease: [0.6, 0.05, -0.01, 0.9] }}
        style={{ pointerEvents: toggleMenu ? "auto" : "none" }}
        aria-hidden={!toggleMenu}
      >
            <Container>
              <NavHeader>
                <Flex spaceBetween noHeight>
                  <h2>Projects</h2>
                  <CloseNav
                    onClick={() => setToggleMenu(!toggleMenu)}
                    onMouseEnter={() => onCursor("pointer")}
                    onMouseLeave={onCursor}
                  >
                    <button>
                      <span></span>
                      <span></span>
                    </button>
                  </CloseNav>
                </Flex>
              </NavHeader>
              <NavList>
                <ul>
                  {navRoutes.map(route => (
                    <motion.li
                      className={revealVideo.key === route.id ? "active" : ""}
                      onMouseEnter={() => onCursor("pointer")}
                      onMouseLeave={onCursor}
                      key={route.id}
                      onClick={() =>
                        setRevealVideo({
                          show: true,
                          video: route.video,
                          key: route.id,
                        })
                      }
                      onHoverStart={() =>
                        setRevealVideo({
                          show: true,
                          video: route.video,
                          key: route.id,
                        })
                      }
                      onHoverEnd={() =>
                        setRevealVideo({
                          show: false,
                          video: route.video,
                          key: route.id,
                        })
                      }
                    >
                      <Link to={`/projects/${route.path}`} onClick={(e) => {e.preventDefault()}}>
                        <motion.div
                          initial={{ x: -108 }}
                          whileHover={{
                            x: -40,
                            transition: {
                              duration: 0.4,
                              ease: [0.6, 0.05, -0.01, 0.9],
                            },
                          }}
                          className="link"
                        >
                          <span className="arrow">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 101 57"
                            >
                              <path
                                d="M33 34H0V24h81.429L66 7.884 73.548 0l19.877 20.763.027-.029L101 28.618 73.829 57l-7.548-7.884L80.753 34H33z"
                                fill="#FFF"
                                fillRule="evenodd"
                              ></path>
                            </svg>
                          </span>
                          {route.title}
                        </motion.div>
                      </Link>
                    </motion.li>
                  ))}
                </ul>
              </NavList>
              <NavFooter>
                <Flex spaceBetween>
                  <FooterContent>
                    <p>info@furrow.studio</p>
                  </FooterContent>
                  <FooterContent wider>
                    <p>902.315.1234</p>
                  </FooterContent>
                  <FooterSocial>
                    <a
                      onMouseEnter={() => onCursor("pointer")}
                      onMouseLeave={onCursor}
                      href="/"
                    >
                      <Instagram />
                    </a>
                    <a
                      onMouseEnter={() => onCursor("pointer")}
                      onMouseLeave={onCursor}
                      href="/"
                    >
                      <Facebook />
                    </a>
                    <a
                      onMouseEnter={() => onCursor("pointer")}
                      onMouseLeave={onCursor}
                      href="/"
                    >
                      <Vimeo />
                    </a>
                  </FooterSocial>
                </Flex>
              </NavFooter>
              <NavVideos>
                <motion.div
                  animate={{ width: revealVideo.show ? 0 : "100%" }}
                  className="reveal"
                >
                  <div className="video">
                    <motion.video
                      ref={videoRef}
                      data-nav-video
                      animate={{ opacity: 1 }}
                      transition={{
                        duration: 0.2,
                        ease: "easeInOut",
                      }}
                      loop
                      autoPlay
                      muted
                      playsInline
                      preload="auto"
                    ></motion.video>
                  </div>
                </motion.div>
              </NavVideos>
            </Container>
      </Nav>
    </>
  )
}

export default Navigation
